import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { formatearFecha } from '@/lib/fecha'
import { Boton } from '@/ui/Boton'
import { CampoFormulario, EntradaTexto, Selector } from '@/ui/CampoFormulario'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { FilasEsqueleto } from '@/ui/Esqueleto'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { Monto } from '@/ui/Numero'
import { usePagos, useProveedores } from '@/dominio/compras/useCompras'
import { MEDIOS_PAGO } from '@/dominio/compras/tipos'
import { EncabezadoPantalla, Tarjeta } from './-pantalla'
import { finDeMesIso, inicioDeMesIso } from './-contabilidad/comun'
import { ErrorCompras, TD, TH } from './-compras/comun'
import { DetallePago, PanelPago, TONO_PAGO } from './-compras/Pagos'

/**
 * Pagos a proveedores (`docs/adr/0028` §4), del plan base: los registrados,
 * los programados y los anulados, con su comprobante en PDF.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const Route = createFileRoute('/pagos')({
  validateSearch: (b: Record<string, unknown>): { pago?: string } => ({
    pago: typeof b.pago === 'string' && UUID.test(b.pago) ? b.pago : undefined,
  }),
  component: PantallaPagos,
})

function PantallaPagos() {
  const navegar = useNavigate({ from: Route.fullPath })
  const { pago: abierto } = Route.useSearch()
  const [desde, setDesde] = useState(inicioDeMesIso())
  const [hasta, setHasta] = useState(finDeMesIso())
  const [proveedor, setProveedor] = useState('')
  const [estado, setEstado] = useState('')
  const proveedores = useProveedores()
  const pagos = usePagos({ desde, hasta, proveedor: proveedor || undefined, estado: estado || undefined })
  const [nuevo, setNuevo] = useState(false)
  const abrir = (id: string | null) => void navegar({ search: { pago: id ?? undefined }, replace: true })

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoPantalla
        titulo="Pagos"
        descripcion="Lo que se le pagó a cada proveedor. Las retenciones que se practican al pagar van al 606 y al IR-17 con la fecha del pago."
        acciones={
          <Boton variante="primario" icono={<Plus />} onClick={() => setNuevo(true)}>
            Nuevo pago
          </Boton>
        }
      />

      <Tarjeta relleno={false}>
        <div className="grid gap-3 p-3 sm:grid-cols-4">
          <CampoFormulario etiqueta="Desde">
            {(a) => <EntradaTexto {...a} type="date" value={desde} max={hasta} onChange={(e) => setDesde(e.target.value)} />}
          </CampoFormulario>
          <CampoFormulario etiqueta="Hasta">
            {(a) => <EntradaTexto {...a} type="date" value={hasta} min={desde} onChange={(e) => setHasta(e.target.value)} />}
          </CampoFormulario>
          <CampoFormulario etiqueta="Proveedor">
            {(a) => (
              <Selector {...a} value={proveedor} onChange={(e) => setProveedor(e.target.value)}>
                <option value="">Todos</option>
                {(proveedores.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </Selector>
            )}
          </CampoFormulario>
          <CampoFormulario etiqueta="Estado">
            {(a) => (
              <Selector {...a} value={estado} onChange={(e) => setEstado(e.target.value)}>
                <option value="">Todos</option>
                <option value="registrado">Registrados</option>
                <option value="programado">Programados</option>
                <option value="anulado">Anulados</option>
              </Selector>
            )}
          </CampoFormulario>
        </div>
        <ErrorCompras error={pagos.error} />
        {!pagos.data ? (
          <div className="p-3">
            <FilasEsqueleto filas={5} columnas={6} />
          </div>
        ) : pagos.data.length === 0 ? (
          <EstadoVacio variante="sin-resultados" mensaje="Sin pagos en el período." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-cuerpo">
              <thead className="bg-gris-50">
                <tr className="border-b border-gris-300">
                  <th className={TH}>Número</th>
                  <th className={TH}>Fecha</th>
                  <th className={TH}>Proveedor</th>
                  <th className={TH}>Medio</th>
                  <th className={`${TH} text-right`}>Monto</th>
                  <th className={`${TH} text-right`}>Aplicado</th>
                  <th className={TH}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {pagos.data.map((p) => (
                  <tr
                    key={p.id}
                    className="h-[var(--alto-fila)] cursor-pointer border-b border-gris-200 last:border-b-0 hover:bg-gris-50"
                    onClick={() => abrir(p.id)}
                  >
                    <td className={`${TD} numero`}>{p.numeroTexto}</td>
                    <td className={`${TD} numero`}>{formatearFecha(p.fechaProgramada ?? p.fecha)}</td>
                    <td className={TD}>{p.proveedorNombre}</td>
                    <td className={`${TD} text-gris-700`}>
                      {p.tipo === 'aplicacion_anticipo' ? 'Anticipo' : (MEDIOS_PAGO.find((m) => m.valor === p.medio)?.nombre ?? '—')}
                      {p.chequeNumero ? <span className="numero ml-1">#{p.chequeNumero}</span> : null}
                    </td>
                    <td className={`${TD} text-right`}>
                      <Monto valor={p.monto} />
                    </td>
                    <td className={`${TD} text-right`}>
                      <Monto valor={p.aplicado} />
                    </td>
                    <td className={TD}>
                      <InsigniaEstado tono={TONO_PAGO[p.estado]}>{p.estado}</InsigniaEstado>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>

      {abierto ? <DetallePago id={abierto} onCerrar={() => abrir(null)} /> : null}
      {nuevo ? <PanelPago proveedorInicial={proveedor || null} documentosIniciales={[]} onCerrar={() => setNuevo(false)} onRegistrado={(id) => abrir(id)} /> : null}
    </div>
  )
}
